import React from 'react';
import { Link } from 'react-router-dom';
import ROUTES from 'utils/routes.js';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details for debugging
        console.error('Error caught by boundary:', error, errorInfo);

        this.setState(prevState => ({
            error,
            errorInfo,
            errorCount: prevState.errorCount + 1
        }));

        // Optionally send error to logging service
        // logErrorToService(error, errorInfo);
    }

    handleReset = () => {
        // Clear error state and attempt to recover
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });

        // Reload the page to ensure clean state
        window.location.reload();
    };

    handleGoHome = () => {
        // Navigate to home and reload
        window.location.href = ROUTES.LESSONS;
    };

    render() {
        if (this.state.hasError) {
            // Too many errors in quick succession - suggest full reset
            const isCritical = this.state.errorCount > 2;

            return (
                <div className="flex items-center justify-center min-h-screen bg-background text-text p-4">
                    <div className="max-w-2xl w-full bg-background-pop rounded-3xl p-8 shadow-lg">
                        <div className="text-center">
                            {/* Error Icon */}
                            <div className="mb-6 flex justify-center">
                                <svg
                                    className="w-24 h-24 text-warning"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>

                            {/* Error Message */}
                            <h1 className="text-3xl font-bold mb-4">
                                {isCritical ? 'Critical Error' : 'Oops! Something went wrong'}
                            </h1>

                            <p className="text-text-light text-lg mb-6">
                                {isCritical
                                    ? 'Multiple errors detected. The app needs to restart to recover.'
                                    : 'Don\'t worry, your progress is saved! Click below to get back on track.'
                                }
                            </p>

                            {/* Error Details (only in development) */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="mb-6 text-left bg-background rounded-lg p-4">
                                    <summary className="cursor-pointer font-bold text-warning mb-2">
                                        Error Details (Development Only)
                                    </summary>
                                    <div className="text-sm font-mono overflow-auto max-h-64">
                                        <p className="text-warning font-bold mb-2">
                                            {this.state.error.toString()}
                                        </p>
                                        <pre className="text-text-light whitespace-pre-wrap">
                                            {this.state.errorInfo?.componentStack}
                                        </pre>
                                    </div>
                                </details>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={this.handleReset}
                                    className="bg-primary hover:bg-primary-dark text-background font-bold py-3 px-8 rounded-2xl shadow-md transition-all duration-300 text-lg"
                                >
                                    {isCritical ? 'Restart App' : 'Try Again'}
                                </button>

                                <button
                                    onClick={this.handleGoHome}
                                    className="bg-accent hover:bg-accent-dark text-background font-bold py-3 px-8 rounded-2xl shadow-md transition-all duration-300 text-lg"
                                >
                                    Go to Lessons
                                </button>
                            </div>

                            {/* Additional Help */}
                            <div className="mt-8 pt-6 border-t border-border">
                                <p className="text-sm text-text-light">
                                    If this problem persists, try clearing your browser cache or{' '}
                                    <Link to={ROUTES.SETTINGS} className="text-primary hover:underline">
                                        contact support
                                    </Link>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
